import { AssetDetails } from 'modules/assetManager/contexts/BaseAssetManagerContext';
import { MessageType, SubscriptionPolicy } from 'modules/graphql/types';
import { SmsSender } from 'modules/sendingPlatforms/types';
import { BaseOption } from 'ui/selects/types';
import { IS_VALID_URL } from 'utils/validationRegex';
import { SmartOptInFieldsInput, SmsSmartOptInLink, SmsSmartOptInLinkErrorMessages } from './types';

export const transformToInput = (
  updatedFields: AssetDetails<SmsSmartOptInLink>,
): SmartOptInFieldsInput => {
  return {
    name: updatedFields?.name,
    description: updatedFields?.description,
    brandName: updatedFields?.brandName,
    messageTypeId: updatedFields?.messageTypeId,
    smsSenderId: updatedFields?.smsSenderId,
    fallbackUrl: updatedFields?.fallbackUrl,
    termsLink: updatedFields?.termsLink,
    privacyLink: updatedFields?.privacyLink,
  };
};

export const getOptInOnlyMessageTypes = (smsMessageTypes: MessageType[] = []) =>
  smsMessageTypes.filter(
    (type) =>
      type?.subscriptionPolicy === SubscriptionPolicy.OptIn ||
      type?.subscriptionPolicy === SubscriptionPolicy.DoubleOptIn,
  );

export const VALIDATE_SMART_LINK_ERROR_MESSAGES = {
  name: 'Enter a name',
  brandName: 'Enter a brand name',
  messageTypeId: 'Select or configure a message type',
  smsSenderId: 'Select or configure a sender',
  termsLink: 'Must be a valid URL with https://',
  privacyLink: 'Must be a valid URL with https://',
  fallbackUrl: 'Must be a valid URL with https://',
} as const;

export const validateSmartLink = (
  link: AssetDetails<SmsSmartOptInLink>,
): SmsSmartOptInLinkErrorMessages => {
  const isNonEmptyString = (v: string) => v && v.trim() !== '';
  const isNonEmptyId = (id: number) => Number.isInteger(id);

  const validators = {
    name: isNonEmptyString,
    brandName: isNonEmptyString,
    messageTypeId: isNonEmptyId,
    smsSenderId: isNonEmptyId,
    termsLink: (v: string) => !v?.length || IS_VALID_URL.test(v),
    privacyLink: (v: string) => !v?.length || IS_VALID_URL.test(v),
    fallbackUrl: (v: string) => isNonEmptyString(v) && IS_VALID_URL.test(v),
  };

  return Object.entries(validators).reduce((errors, [key, validator]) => {
    return {
      ...errors,
      ...(validator(link[key as keyof SmsSmartOptInLink] as never)
        ? null
        : {
            [key]:
              VALIDATE_SMART_LINK_ERROR_MESSAGES[
                key as keyof typeof VALIDATE_SMART_LINK_ERROR_MESSAGES
              ],
          }),
    };
  }, {});
};

export const formatSenderOptions = (senders: SmsSender[] = []): BaseOption[] =>
  senders.map((sender) => ({
    id: sender.id.toString(),
    value: sender.id.toString(),
    label: sender.name,
  }));

export const formatMessageTypeOptions = (messageTypes: MessageType[] = []): BaseOption[] =>
  messageTypes.map((messageType) => ({
    id: messageType.id.toString(),
    value: messageType.id.toString(),
    label: messageType.name,
  }));
